import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Denuncia {
    id_denuncia: number; // Campo correto da tabela
    id_denunciante: number;
    tipo_denunciante: 'banda' | 'usuario';
    reported_item_id: number;
    reported_item_type: string;
    razao: string;
    descricao?: string;
    status: 'pendente' | 'aprovada' | 'rejeitada';
    created_at: string;
    updated_at?: string;
    nome_denunciante?: string;
    item_details?: string;
    admin_id?: number;
    // Novos campos de contato
    email_denunciante?: string;
    telefone_denunciante?: string;
}

const DenunciasAdmin: React.FC = () => {
    const navigate = useNavigate();
    const [denuncias, setDenuncias] = useState<{ pendente: Denuncia[]; aprovada: Denuncia[]; rejeitada: Denuncia[] }>({
        pendente: [],
        aprovada: [],
        rejeitada: [],
    });
    const [expandedStatus, setExpandedStatus] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDenuncia, setSelectedDenuncia] = useState<Denuncia | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        fetchDenuncias();
    }, []);

    const fetchDenuncias = async () => {
        setLoading(true);
        setError(null);
        try {
            console.log('🔄 Fazendo requisição para buscar denúncias...');
            const response = await api.get('/denuncias/reports');

            console.log('📦 Resposta completa do servidor:', response);
            console.log('⚠️ Dados das denúncias:', response.data);

            // Agrupar denúncias por status
            const denunciasAgrupadas = {
                pendente: response.data.filter((d: Denuncia) => d.status === 'pendente') || [],
                aprovada: response.data.filter((d: Denuncia) => d.status === 'aprovada') || [],
                rejeitada: response.data.filter((d: Denuncia) => d.status === 'rejeitada') || [],
            };

            if (denunciasAgrupadas.pendente) {
                console.log('⏳ Denúncias pendentes:', denunciasAgrupadas.pendente);
            }
            if (denunciasAgrupadas.aprovada) {
                console.log('✅ Denúncias aprovadas:', denunciasAgrupadas.aprovada);
            }
            if (denunciasAgrupadas.rejeitada) {
                console.log('❌ Denúncias rejeitadas:', denunciasAgrupadas.rejeitada);
            }

            setDenuncias(denunciasAgrupadas);
        } catch (err) {
            console.error('❌ Erro ao carregar denúncias:', err);

            const axiosErr = err as any;
            if (axiosErr.response) {
                console.error('📡 Status da resposta:', axiosErr.response?.status);
                console.error('📡 Dados da resposta de erro:', axiosErr.response?.data);
                console.error('📡 Headers da resposta:', axiosErr.response?.headers);
            }

            setError('Erro ao carregar denúncias.');
        } finally {
            setLoading(false);
        }
    };

    const updateDenunciaStatus = async (id: number, status: 'aprovada' | 'rejeitada') => {
        try {
            console.log(`🔄 Atualizando status da denúncia ${id} para "${status}"`);

            const response = await api.put(
                `/denuncias/reports/${id}/status`,
                { status }
            );

            console.log('📦 Resposta da atualização de status:', response);

            if (response.status === 200) {
                alert(`Status da denúncia atualizado para "${status}" com sucesso!`);
                fetchDenuncias(); // Atualiza a lista após a mudança de status
                setIsModalOpen(false); // Fecha o modal após atualizar
            } else {
                alert('Falha ao atualizar status da denúncia.');
            }
        } catch (err) {
            console.error('❌ Erro ao atualizar status:', err);
            alert('Erro ao atualizar status da denúncia. Verifique sua conexão e tente novamente.');
        }
    };

    const toggleStatus = (status: string) => {
        console.log(`🔄 Alternando visualização do status: ${status}`);
        setExpandedStatus(expandedStatus === status ? null : status);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('pt-BR');
    };

    const getRazaoText = (razao: string) => {
        const razoes: { [key: string]: string } = {
            'spam': 'Spam',
            'conteudo_inapropriado': 'Conteúdo Inapropriado',
            'violacao_direitos_autorais': 'Violação de Direitos Autorais',
            'assedio': 'Assédio',
            'informacoes_falsas': 'Informações Falsas',
            'outros': 'Outros'
        };
        return razoes[razao] || razao;
    };

    const openModal = (denuncia: Denuncia) => {
        setSelectedDenuncia(denuncia);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedDenuncia(null);
        setIsModalOpen(false);
    };

    // Filtrar denúncias por busca
    const filterDenuncias = (denunciasList: Denuncia[]) => {
        if (!searchTerm) return denunciasList;

        return denunciasList.filter(denuncia =>
            denuncia.razao.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (denuncia.descricao?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (denuncia.nome_denunciante?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            denuncia.reported_item_type.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    // Calcular totais
    const totalDenuncias = Object.values(denuncias).flat().length;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <button onClick={() => navigate('/admin')} style={styles.backButton}>
                    ← Voltar ao Painel
                </button>
                <h1 style={styles.title}>Gerenciamento de Denúncias</h1>
            </div>

            {/* Busca */}
            <div style={styles.searchContainer}>
                <input
                    type="text"
                    placeholder="Buscar por razão, descrição, denunciante ou tipo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
            </div>

            {/* Estatísticas */}
            <div style={styles.statsContainer}>
                <div style={styles.statCard}>
                    <span style={styles.statNumber}>{totalDenuncias}</span>
                    <span style={styles.statLabel}>Total</span>
                </div>
                <div style={styles.statCard}>
                    <span style={styles.statNumber}>{denuncias.pendente.length}</span>
                    <span style={styles.statLabel}>Pendentes</span>
                </div>
                <div style={styles.statCard}>
                    <span style={styles.statNumber}>{denuncias.aprovada.length}</span>
                    <span style={styles.statLabel}>Aprovadas</span>
                </div>
                <div style={styles.statCard}>
                    <span style={styles.statNumber}>{denuncias.rejeitada.length}</span>
                    <span style={styles.statLabel}>Rejeitadas</span>
                </div>
            </div>

            {loading && <p style={styles.message}>Carregando...</p>}
            {error && <p style={{ ...styles.message, color: '#dc3545' }}>{error}</p>}

            {!loading && (
                <div>
                    {(['pendente', 'aprovada', 'rejeitada'] as const).map((status) => {
                        const denunciasFiltradas = filterDenuncias(denuncias[status] || []);
                        return (
                            <div key={status} style={styles.statusSection}>
                                <button
                                    style={{
                                        ...styles.statusButton,
                                        backgroundColor: expandedStatus === status ? '#ff6600' : '#f2f2f2',
                                        color: expandedStatus === status ? 'white' : '#333',
                                    }}
                                    onClick={() => toggleStatus(status)}
                                >
                                    {status.charAt(0).toUpperCase() + status.slice(1)} ({denunciasFiltradas.length})
                                </button>
                                {expandedStatus === status && (
                                    <div style={styles.denunciasList}>
                                        {denunciasFiltradas.map((denuncia) => (
                                            <div key={denuncia.id_denuncia} style={styles.denunciaCard}>
                                                <div style={styles.denunciaInfo}>
                                                    <div style={styles.denunciaHeader}>
                                                        <span style={styles.denunciaId}>#{denuncia.id_denuncia}</span>
                                                        <span style={styles.denunciaDate}>
                                                            {formatDate(denuncia.created_at)}
                                                        </span>
                                                    </div>

                                                    <div style={styles.denunciaContent}>
                                                        <p style={styles.denunciaRow}>
                                                            <strong>Denunciante:</strong> {denuncia.nome_denunciante || `${denuncia.tipo_denunciante} #${denuncia.id_denunciante}`}
                                                            <span style={styles.tipoDenunciante}>({denuncia.tipo_denunciante})</span>
                                                        </p>

                                                        {/* Informações de contato */}
                                                        {(denuncia.email_denunciante || denuncia.telefone_denunciante) && (
                                                            <div style={styles.contatoContainer}>
                                                                {denuncia.email_denunciante && (
                                                                    <p style={styles.contatoRow}>
                                                                        <strong>📧 Email:</strong>
                                                                        <a href={`mailto:${denuncia.email_denunciante}`} style={styles.contatoLink}>
                                                                            {denuncia.email_denunciante}
                                                                        </a>
                                                                    </p>
                                                                )}
                                                                {denuncia.telefone_denunciante && (
                                                                    <p style={styles.contatoRow}>
                                                                        <strong>📱 WhatsApp:</strong>
                                                                        <a
                                                                            href={`https://wa.me/${denuncia.telefone_denunciante.replace(/\D/g, '')}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={styles.contatoLink}
                                                                        >
                                                                            {denuncia.telefone_denunciante}
                                                                        </a>
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}

                                                        <p style={styles.denunciaRow}>
                                                            <strong>Item:</strong> {denuncia.reported_item_type} #{denuncia.reported_item_id}
                                                            {denuncia.item_details && (
                                                                <span style={styles.itemDetails}> - {denuncia.item_details}</span>
                                                            )}
                                                        </p>

                                                        <p style={styles.denunciaRow}>
                                                            <strong>Razão:</strong> {getRazaoText(denuncia.razao)}
                                                        </p>

                                                        {denuncia.descricao && (
                                                            <p style={styles.denunciaRow}>
                                                                <strong>Descrição:</strong> {denuncia.descricao.substring(0, 100)}
                                                                {denuncia.descricao.length > 100 && '...'}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div style={styles.actions}>
                                                    <button
                                                        style={styles.detailButton}
                                                        onClick={() => openModal(denuncia)}
                                                    >
                                                        Ver Detalhes
                                                    </button>

                                                    {status === 'pendente' && (
                                                        <>
                                                            <button
                                                                style={{ ...styles.actionButton, backgroundColor: '#28a745' }}
                                                                onClick={() => updateDenunciaStatus(denuncia.id_denuncia, 'aprovada')}
                                                            >
                                                                Aprovar
                                                            </button>
                                                            <button
                                                                style={{ ...styles.actionButton, backgroundColor: '#dc3545' }}
                                                                onClick={() => updateDenunciaStatus(denuncia.id_denuncia, 'rejeitada')}
                                                            >
                                                                Rejeitar
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        {denunciasFiltradas.length === 0 && (
                                            <p style={styles.message}>
                                                {searchTerm ? 'Nenhuma denúncia encontrada com os termos de busca.' : 'Nenhuma denúncia encontrada.'}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal de Detalhes */}
            {isModalOpen && selectedDenuncia && (
                <div style={styles.modalOverlay} onClick={closeModal}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>Detalhes da Denúncia #{selectedDenuncia.id_denuncia}</h2>
                            <button onClick={closeModal} style={styles.closeButton}>
                                ✕
                            </button>
                        </div>

                        <div style={styles.modalContent}>
                            <div style={styles.modalSection}>
                                <div style={styles.statusBadge}>
                                    Status: {selectedDenuncia.status.charAt(0).toUpperCase() + selectedDenuncia.status.slice(1)}
                                </div>
                                <div style={styles.modalDate}>
                                    Criado em: {formatDate(selectedDenuncia.created_at)}
                                    {selectedDenuncia.updated_at && (
                                        <><br />Atualizado em: {formatDate(selectedDenuncia.updated_at)}</>
                                    )}
                                </div>
                            </div>

                            <div style={styles.modalSection}>
                                <h3 style={styles.sectionTitle}>Informações do Denunciante</h3>
                                <p><strong>Nome:</strong> {selectedDenuncia.nome_denunciante || 'Não disponível'}</p>
                                <p><strong>Tipo:</strong> {selectedDenuncia.tipo_denunciante === 'banda' ? 'Banda' : 'Usuário'}</p>
                                <p><strong>ID:</strong> {selectedDenuncia.id_denunciante}</p>

                                {/* Informações de contato no modal */}
                                {(selectedDenuncia.email_denunciante || selectedDenuncia.telefone_denunciante) && (
                                    <div style={styles.contatoModalContainer}>
                                        <h4 style={styles.contatoModalTitle}>📞 Contatos para Suporte:</h4>
                                        {selectedDenuncia.email_denunciante && (
                                            <p style={styles.contatoModalRow}>
                                                <strong>📧 Email:</strong>
                                                <a href={`mailto:${selectedDenuncia.email_denunciante}`} style={styles.contatoLink}>
                                                    {selectedDenuncia.email_denunciante}
                                                </a>
                                            </p>
                                        )}
                                        {selectedDenuncia.telefone_denunciante && (
                                            <p style={styles.contatoModalRow}>
                                                <strong>📱 WhatsApp:</strong>
                                                <a
                                                    href={`https://wa.me/${selectedDenuncia.telefone_denunciante.replace(/\D/g, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={styles.contatoLink}
                                                >
                                                    {selectedDenuncia.telefone_denunciante}
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={styles.modalSection}>
                                <h3 style={styles.sectionTitle}>Item Denunciado</h3>
                                <p><strong>Tipo:</strong> {selectedDenuncia.reported_item_type}</p>
                                <p><strong>ID:</strong> {selectedDenuncia.reported_item_id}</p>
                                {selectedDenuncia.item_details && (
                                    <p><strong>Detalhes:</strong> {selectedDenuncia.item_details}</p>
                                )}
                            </div>

                            <div style={styles.modalSection}>
                                <h3 style={styles.sectionTitle}>Motivo da Denúncia</h3>
                                <div style={styles.reasonBox}>
                                    <strong>Razão:</strong> {getRazaoText(selectedDenuncia.razao)}
                                </div>
                                {selectedDenuncia.descricao && (
                                    <div style={styles.descriptionBox}>
                                        <strong>Descrição:</strong>
                                        <p style={styles.descriptionText}>{selectedDenuncia.descricao}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {selectedDenuncia.status === 'pendente' && (
                            <div style={styles.modalActions}>
                                <button
                                    onClick={() => updateDenunciaStatus(selectedDenuncia.id_denuncia, 'aprovada')}
                                    style={{ ...styles.actionButton, backgroundColor: '#28a745' }}
                                >
                                    ✓ Aprovar Denúncia
                                </button>
                                <button
                                    onClick={() => updateDenunciaStatus(selectedDenuncia.id_denuncia, 'rejeitada')}
                                    style={{ ...styles.actionButton, backgroundColor: '#dc3545' }}
                                >
                                    ✗ Rejeitar Denúncia
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#f9f9f9',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '30px',
        gap: '20px',
    },
    backButton: {
        padding: '10px 15px',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    title: {
        fontSize: '24px',
        color: '#333',
        margin: 0,
        fontWeight: 'bold',
    },
    searchContainer: {
        marginBottom: '20px',
    },
    searchInput: {
        width: '100%',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '16px',
        boxSizing: 'border-box' as 'border-box',
    },
    statsContainer: {
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        flexWrap: 'wrap' as 'wrap',
    },
    statCard: {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        textAlign: 'center' as 'center',
        minWidth: '120px',
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '5px',
    },
    statNumber: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#333',
    },
    statLabel: {
        fontSize: '14px',
        color: '#666',
    },
    message: {
        fontSize: '16px',
        textAlign: 'center' as 'center',
        color: '#555',
    },
    statusSection: {
        marginBottom: '20px',
    },
    statusButton: {
        width: '100%',
        padding: '10px',
        textAlign: 'left' as 'left',
        fontSize: '18px',
        fontWeight: 'bold',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        marginBottom: '10px',
        transition: 'background-color 0.3s ease',
    },
    denunciasList: {
        paddingLeft: '10px',
    },
    denunciaCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '15px',
        marginBottom: '10px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    denunciaInfo: {
        flex: '1',
        marginRight: '20px',
    },
    denunciaHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
        paddingBottom: '5px',
        borderBottom: '1px solid #e9ecef',
    },
    denunciaId: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333',
    },
    denunciaDate: {
        fontSize: '12px',
        color: '#666',
    },
    denunciaContent: {
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '5px',
    },
    denunciaRow: {
        fontSize: '14px',
        lineHeight: '1.4',
        margin: '3px 0',
    },
    tipoDenunciante: {
        color: '#666',
        fontSize: '12px',
        marginLeft: '5px',
    },
    itemDetails: {
        color: '#666',
        fontSize: '12px',
    },
    actions: {
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '10px',
        flexShrink: 0,
    },
    detailButton: {
        padding: '8px 12px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    actionButton: {
        padding: '8px 12px',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background-color 0.3s ease',
    },
    // Estilos adicionais para contato
    contatoContainer: {
        marginTop: '8px',
        padding: '8px',
        backgroundColor: '#e8f5e8',
        borderRadius: '4px',
        borderLeft: '3px solid #28a745',
    },
    contatoRow: {
        fontSize: '12px',
        margin: '3px 0',
        lineHeight: '1.4',
    },
    contatoLink: {
        color: '#007bff',
        textDecoration: 'none',
        marginLeft: '5px',
        fontWeight: 'bold',
    },
    contatoModalContainer: {
        marginTop: '15px',
        padding: '12px',
        backgroundColor: '#f0f8ff',
        borderRadius: '6px',
        border: '1px solid #007bff',
    },
    contatoModalTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '8px',
        marginTop: 0,
    },
    contatoModalRow: {
        fontSize: '13px',
        margin: '5px 0',
        lineHeight: '1.4',
    },
    // Modal styles
    modalOverlay: {
        position: 'fixed' as 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px',
        borderBottom: '1px solid #e9ecef',
    },
    modalTitle: {
        margin: 0,
        fontSize: '20px',
        color: '#333',
        fontWeight: 'bold',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        color: '#666',
        padding: '5px',
    },
    modalContent: {
        padding: '20px',
    },
    modalSection: {
        marginBottom: '20px',
        padding: '15px',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
    },
    statusBadge: {
        padding: '6px 12px',
        backgroundColor: '#007bff',
        color: 'white',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: 'bold',
        display: 'inline-block',
        marginBottom: '10px',
    },
    modalDate: {
        fontSize: '14px',
        color: '#666',
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '10px',
        marginTop: 0,
    },
    reasonBox: {
        padding: '10px',
        backgroundColor: '#fff3cd',
        borderRadius: '4px',
        fontSize: '14px',
        marginBottom: '10px',
    },
    descriptionBox: {
        padding: '10px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        fontSize: '14px',
    },
    descriptionText: {
        margin: '5px 0 0 0',
        lineHeight: '1.4',
    },
    modalActions: {
        display: 'flex',
        gap: '10px',
        padding: '20px',
        borderTop: '1px solid #e9ecef',
        justifyContent: 'flex-end',
    },
};

export default DenunciasAdmin;